import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {Logger} from '../../class/helper/logger.class';
import {IRecordEmit} from '../../interfaces/record';
import {cloneDeep} from 'lodash';
import {ServiceAction, ServiceState} from '../../models/service';
import {ActivatedRoute, Params} from '@angular/router';
import {StorageService} from '../../services/storage/storage.service';
import {Loader} from '../../models/loader/loader.redux';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {ExerciseTimeSinceStartEnum, PhysicalActivityLevelEnum} from '../../enums/record';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {AuthState} from 'src/app/models/auth/auth.redux';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Store} from '@ngxs/store';
import {IRecordPhysicalActivity, RecordPhysicalActivityAction, RecordPhysicalActivityState} from '../../models/record-physical-activity';
import {IUser} from '../../models/auth';
import {SkeletonEnum} from '../../enums/skeleton';
import {SkeletonService} from '../../services/skeleton/skeleton.service';

@Component({
  selector: 'app-record-history-physical-activity',
  templateUrl: './record-history-physical-activity.component.html',
  styleUrls: ['./record-history-physical-activity.component.scss'],
  animations: [fadeInAnimation]
})
export class RecordHistoryPhysicalActivityComponent implements OnInit, OnDestroy {

  isPatient$: Observable<boolean> = this.store.select<boolean>(AuthState.getIsPatient);
  wasDeployed$: Observable<boolean> = this.store.select<boolean>(RecordPhysicalActivityState.getWasDeployed);
  @Input() recordPhysicalActivity: IRecordPhysicalActivity;
  @Output() recordPhysicalActivityChange = new EventEmitter<IRecordEmit>();
  recordPhysicalActivityRedux: IRecordPhysicalActivity;

  form: UntypedFormGroup;

  exerciseTimeSinceStartEnum = ExerciseTimeSinceStartEnum;
  physicalActivityLevelEnum = PhysicalActivityLevelEnum;

  serviceId: string;
  expertId: string;
  patientId: string;
  clinicId: string;

  userLogged: IUser;
  disableForm: boolean;
  loadInfo: boolean;

  wasDeployedSubscription: Subscription;

  skeletonTemplate: any[] = [];
  constructor(
    private store: Store,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private utilitiesService: UtilitiesService,
    private messageAlert: MessageAlertService,
    private skeletonService: SkeletonService,
  ) {
    this.disableForm = false;
    this.loadInfo = false;

    this.wasDeployedSubscription = this.wasDeployed$.subscribe(value => {
      if (value && !this.recordPhysicalActivity) {
        this.loadInfo = false;
        this.initValuesHttp().then();
      }
    });
  }

  ngOnInit(): void {
    this.initValues();
  }

  initValues(): void {
    this.form = new UntypedFormGroup({});
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS);
  }

  async initValuesHttp(): Promise<void> {
    try {
      this.getParams();
      this.userLogged = cloneDeep(this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user);
      if (this.userLogged && this.userLogged.experts) {
        this.expertId = this.userLogged.experts[0]._id;
      } else if (this.userLogged && this.userLogged.patients) {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.expertId = queryParams.expert;
      }
      this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      if (!this.serviceId) {
        await firstValueFrom(this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId)));
        this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      }
      await firstValueFrom(this.store.dispatch(new RecordPhysicalActivityAction.GetActive(this.serviceId, this.patientId)));
      this.recordPhysicalActivityRedux = cloneDeep(await firstValueFrom(this.store.selectOnce(RecordPhysicalActivityState.getRecordPhysicalActivity)));
      this.recordPhysicalActivity = cloneDeep(this.recordPhysicalActivityRedux);
      this.recordPhysicalActivityChange.emit({ record: this.recordPhysicalActivity, updated: false });
      this.setFormProperties(this.recordPhysicalActivityRedux);
      this.loadInfo = true;
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  setFormProperties(recordPhysicalActivity: IRecordPhysicalActivity): void {
    this.disableForm = !this.userLogged?.experts;
    this.form = new UntypedFormGroup({
      occupation: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.occupation : null),
      exercises: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exercises : false),
      exerciseIntensity: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exerciseIntensity : null),
      physicalLevelActivity: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.physicalLevelActivity : null),
      exerciseTimeSinceStart: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exerciseTimeSinceStart : null),
      exercisePerDay: new UntypedFormGroup({
        monday: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exercisePerDay?.monday : 0),
        tuesday: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exercisePerDay?.tuesday : 0),
        wednesday: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exercisePerDay?.wednesday : 0),
        thursday: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exercisePerDay?.thursday : 0),
        friday: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exercisePerDay?.friday : 0),
        saturday: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exercisePerDay?.saturday : 0),
        sunday: new UntypedFormControl(recordPhysicalActivity ? recordPhysicalActivity.exercisePerDay?.sunday : 0),
      }),
      howManyHoursSpendSittingOrRelaxADay: new UntypedFormControl(
        recordPhysicalActivity ? recordPhysicalActivity.howManyHoursSpendSittingOrRelaxADay : null
      ),
      howManyHoursSpendSittingPerStudySchoolWorkMovingADay: new UntypedFormControl(
        recordPhysicalActivity ? recordPhysicalActivity.howManyHoursSpendSittingPerStudySchoolWorkMovingADay : null
      ),
      howManyStepsWalkADay: new UntypedFormControl(recordPhysicalActivity?.howManyStepsWalkADay ?? 0)
    });
    if (!this.userLogged.experts) {
      this.form.disable();
    }
  }

  getParams(): void {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  onChangeDoExercises(value: boolean): void {
    if (value) {
      this.form.markAsUntouched();
    }
    this.form.get('exerciseIntensity').setValidators(value ? [Validators.required] : []);
    this.form.get('physicalLevelActivity').setValidators(value ? [Validators.required] : []);
    this.form.get('exerciseTimeSinceStart').setValidators(value ? [Validators.required] : []);
    this.form.updateValueAndValidity();
  }

  emitChangeUpdate(): void {
    this.recordPhysicalActivity.updatedAt = new Date().toISOString();
    this.recordPhysicalActivityChange.emit({ record: this.recordPhysicalActivity, updated: true });
  }

  async onSave(): Promise<void> {
    this.form.markAllAsTouched();
    this.form.markAsDirty();

    try {
      await firstValueFrom(this.store.dispatch(new Loader(true, 'Guardando datos ...')));
      if (!this.recordPhysicalActivityRedux?._id) {
        await firstValueFrom(this.store.dispatch(new RecordPhysicalActivityAction.Post(this.serviceId, this.patientId, this.form.getRawValue())));
        const otherHabitsCreated: IRecordPhysicalActivity = cloneDeep(
          await firstValueFrom(this.store.selectOnce(RecordPhysicalActivityState.getRecordPhysicalActivity)));
        if (!this.recordPhysicalActivity) {
          this.recordPhysicalActivity = cloneDeep(otherHabitsCreated);
        }
        this.recordPhysicalActivityRedux._id = otherHabitsCreated?._id || null;
      } else {
        await firstValueFrom(this.store.dispatch(new RecordPhysicalActivityAction.PatchActive(
          this.serviceId, this.patientId, this.recordPhysicalActivityRedux._id, this.form.getRawValue())
        ));
      }
      this.messageAlert.createMessage('success', 'Historial de actividad física guardado exitosamente.');
      this.emitChangeUpdate();
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
      this.messageAlert.createMessage('error', 'Ha ocurrido un error al actualizar los datos. Intente más tarde', 5000);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  ngOnDestroy(): void {
    this.wasDeployedSubscription.unsubscribe();
  }
}
